import { useRef, useState } from 'react'
import s from '../inputFieldAddressLookup/styles.module.scss'
import { GooglePlacesAutocomplete, GooglePlaceDetails, GooglePlaceSuggestion, GooglePlaceAddress } from '../../../services'

interface InputFieldGoogleAddressLookupProps {
    fieldName: string
    onSelect: (address: GooglePlaceAddress, label: string) => void
    enhanced?: boolean
    label?: string
    className?: string
}

const newSessionToken = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export const InputFieldGoogleAddressLookup = (props: InputFieldGoogleAddressLookupProps) => {
    const { fieldName, onSelect, enhanced = false, label, className } = props
    const [value, setValue] = useState('')
    const [error, setError] = useState('')
    const [lookupResults, setLookupResults] = useState<GooglePlaceSuggestion[]>([])
    const [isSearching, setIsSearching] = useState(false)
    const [focused, setFocused] = useState(false)
    const sessionToken = useRef(newSessionToken())
    const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

    const search = async (searchTerm: string) => {
        setIsSearching(true)
        const results = await GooglePlacesAutocomplete(searchTerm, sessionToken.current)
        setIsSearching(false)
        if (!results.length) {
            setError("Couldn't find address.")
            setLookupResults([])
            return
        }
        setError('')
        setLookupResults(results)
    }

    const onChangeFunc = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.currentTarget.value
        setValue(newValue)

        if (searchTimer.current) clearTimeout(searchTimer.current)
        if (newValue.length > 3) {
            searchTimer.current = setTimeout(() => search(newValue), 300)
        } else {
            setLookupResults([])
        }
    }

    const selectAddress = async (item: GooglePlaceSuggestion) => {
        setLookupResults([])
        setValue(item.label)
        const address = await GooglePlaceDetails(item.placeId, sessionToken.current)
        // A session ends once a place is retrieved, so start a fresh one for any further searching
        sessionToken.current = newSessionToken()
        if (!address) {
            setError("Couldn't load that address, please enter it manually.")
            return
        }
        setError('')
        onSelect(address, item.label)
    }

    return (
        <div className={s.fieldWrapper}>
            <div className={`${s.lookupContainer} ${className || ''}`} style={{ position: 'relative', zIndex: 100000 }}>
                <div className={s.inputWrapper}>
                    <input
                        id={`field-${fieldName}`}
                        className={`${s.inputStyle} ${enhanced ? s.enhanced : ''}`}
                        type={'text'}
                        name={fieldName}
                        value={value}
                        onChange={onChangeFunc}
                        onClick={() => value.length > 3 && !lookupResults.length && search(value)}
                        placeholder={label ? undefined : 'Start typing your street and house number...'}
                        // Browser autofill offers their saved street address, like the UK postcode search
                        autoComplete="address-line1"
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                    />
                    {label && (
                        <label
                            htmlFor={`field-${fieldName}`}
                            className={`${s.label} ${focused || value !== '' ? s.labelRaised : ''}`}
                        >
                            {label}
                        </label>
                    )}
                    {enhanced && (
                        <div className={s.searchIcon}>
                            {isSearching ? (
                                <div className={s.loadingSpinner}></div>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            )}
                        </div>
                    )}
                </div>
                {error && <span className={`${s.notice}`}>{error}</span>}
                {lookupResults.length > 0 ? (
                    <div
                        className={`${s.lookupSelection} ${enhanced ? s.enhancedLookup : ''}`}
                        style={{ position: 'relative', zIndex: 100000 }}
                    >
                        <p>Please select address:</p>
                        <ul>
                            {lookupResults.map((item) => (
                                <li key={item.placeId} style={{ zIndex: 100000 }} onClick={() => selectAddress(item)}>
                                    {item.label}
                                </li>
                            ))}
                        </ul>
                        <p className={s.attribution}>Powered by Google</p>
                    </div>
                ) : null}
            </div>
        </div>
    )
}
